import { createContext, useContext, useMemo, useState, useEffect, ReactNode } from 'react';
import {
  clearLegacyToken,
  getCurrentLegacyUser,
  getStoredLegacyToken,
  loginLegacy,
} from '../services/legacyAuthService';
import { LegacyJwtClaims } from '../types/legacy';

interface AuthContextType {
  user: LegacyJwtClaims | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  /** Superadmin: determinato esclusivamente da flag o ruoli espliciti. */
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<LegacyJwtClaims | null>(null);

  useEffect(() => {
    const token = getStoredLegacyToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    const currentUser = getCurrentLegacyUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = async (username: string, password: string) => {
    setLoading(true);
    try {
      const claims = await loginLegacy(username, password);
      setUser(claims);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    clearLegacyToken();
    setUser(null);
  };

  const isSuperAdmin = useMemo(() => {
    if (!user) return false;

    const role = String((user as Record<string, unknown>).role || '').toLowerCase();
    const roles = Array.isArray((user as Record<string, unknown>).roles)
      ? ((user as Record<string, unknown>).roles as unknown[]).map((item) => String(item).toLowerCase())
      : [];
    const authorities = Array.isArray((user as Record<string, unknown>).authorities)
      ? ((user as Record<string, unknown>).authorities as unknown[]).map((item) => String(item).toLowerCase())
      : [];

    const hasSuperFlag = Boolean(
      user.is_super
      || (user as Record<string, unknown>).super
      || role.includes('super')
      || roles.some((value) => value.includes('super'))
      || authorities.some((value) => value.includes('super')),
    );
    return hasSuperFlag;
  }, [user]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      loading,
      login,
      logout,
      isAdmin: Boolean(user),
      isSuperAdmin,
    }),
    [user, loading, isSuperAdmin],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
